/**
 * Opsora — number formatters.
 *
 * Import from here. Never format inline in components. Every ₹, %, HMR,
 * litre, date, or relative time across the app passes through one of these.
 */

const DASH = "—";

const safe = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));

/**
 * Indian currency. Compact mode shows ₹6.4L / ₹1.2Cr / ₹3.4K.
 */
export const formatINR = (value, opts = {}) => {
  const { compact = false, decimals = 0, showSymbol = true } = opts;
  if (!safe(value)) return DASH;
  const n = Number(value);
  const prefix = showSymbol ? "₹" : "";

  if (compact) {
    if (Math.abs(n) >= 10_000_000) return `${prefix}${(n / 10_000_000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100_000) return `${prefix}${(n / 100_000).toFixed(1)} L`;
    if (Math.abs(n) >= 1_000) return `${prefix}${(n / 1_000).toFixed(1)}K`;
  }
  return new Intl.NumberFormat("en-IN", {
    style: showSymbol ? "currency" : "decimal",
    currency: "INR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
};

/** Percentage — 67.3% */
export const formatPct = (value, decimals = 1) => {
  if (!safe(value)) return DASH;
  return `${Number(value).toFixed(decimals)}%`;
};

/** HMR (Hour Meter Reading) — 1,490 hrs · 1.5K hrs (compact) */
export const formatHMR = (value, compact = false) => {
  if (!safe(value)) return DASH;
  const n = Number(value);
  if (compact && n >= 1000) return `${(n / 1000).toFixed(1)}K hrs`;
  return `${new Intl.NumberFormat("en-IN").format(Math.round(n))} hrs`;
};

/** Litres — 8,240 L  ·  8.2K L (compact) */
export const formatLitres = (value, compact = false) => {
  if (!safe(value)) return DASH;
  const n = Number(value);
  if (compact && n >= 1000) return `${(n / 1000).toFixed(1)}K L`;
  return `${new Intl.NumberFormat("en-IN").format(Math.round(n))} L`;
};

/** Temperature — 103°C */
export const formatTemp = (value) => {
  if (!safe(value)) return DASH;
  return `${Math.round(Number(value))}°C`;
};

/** Indian-grouped large count — 1,00,000 */
export const formatCount = (value) => {
  if (!safe(value)) return DASH;
  return new Intl.NumberFormat("en-IN").format(Number(value));
};

/** Auto-magnitude — used in tight KPI tiles. Optional unit suffix. */
export const formatSmart = (value, unit = "") => {
  if (!safe(value)) return DASH;
  const n = Number(value);
  const suffix = unit ? ` ${unit}` : "";
  if (Math.abs(n) >= 10_000_000) return `${(n / 10_000_000).toFixed(2)} Cr${suffix}`;
  if (Math.abs(n) >= 100_000) return `${(n / 100_000).toFixed(1)} L${suffix}`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K${suffix}`;
  return `${Math.round(n)}${suffix}`;
};

/** Days — "3 days" with optional ⚠ if breach threshold exceeded */
export const formatDays = (days, slaThreshold = null) => {
  if (!safe(days)) return DASH;
  const rounded = Math.round(Number(days));
  const label = `${rounded} day${rounded !== 1 ? "s" : ""}`;
  if (slaThreshold && rounded > slaThreshold) return `${label} ⚠`;
  return label;
};

/** Date — "27 Mar 2018" (unambiguous, no MM/DD vs DD/MM confusion) */
export const formatDate = (dateStr) => {
  if (!dateStr) return DASH;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Relative time — "14m ago", "2h ago", "3d ago" */
export const formatRelative = (dateStr) => {
  if (!dateStr) return DASH;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return DASH;
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.max(0, Math.round(diff))}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};
